import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { AppShell } from './AppShell';
import { useWorkspace } from '../providers/WorkspaceProvider';

const Dashboard = lazy(() => import('../components/Dashboard').then((module) => ({ default: module.Dashboard })));
const Workspaces = lazy(() => import('../components/Workspaces').then((module) => ({ default: module.Workspaces })));
const Profile = lazy(() => import('../components/Profile').then((module) => ({ default: module.Profile })));
const TeamManagement = lazy(() => import('../components/TeamManagement').then((module) => ({ default: module.TeamManagement })));
const ChangePassword = lazy(() => import('../components/ChangePassword').then((module) => ({ default: module.ChangePassword })));
const AuditLogs = lazy(() => import('../components/AuditLogs').then((module) => ({ default: module.AuditLogs })));
const Creators = lazy(() => import('../components/Creators').then((module) => ({ default: module.Creators })));
const SocialAccounts = lazy(() => import('../components/SocialAccounts').then((module) => ({ default: module.SocialAccounts })));
const Campaigns = lazy(() => import('../components/Campaigns').then((module) => ({ default: module.Campaigns })));
const ContentCalendar = lazy(() => import('../components/ContentCalendar').then((module) => ({ default: module.ContentCalendar })));
const CreatePostHub = lazy(() => import('../components/CreatePostHub').then((module) => ({ default: module.CreatePostHub })));
const ReviewQueue = lazy(() => import('../components/ReviewQueue').then((module) => ({ default: module.ReviewQueue })));
const PublishJobs = lazy(() => import('../components/PublishJobs').then((module) => ({ default: module.PublishJobs })));
const Analytics = lazy(() => import('../components/Analytics').then((module) => ({ default: module.Analytics })));
const AssetLibrary = lazy(() => import('../components/AssetLibrary').then((module) => ({ default: module.AssetLibrary })));
const SettingsHub = lazy(() => import('../components/SettingsHub').then((module) => ({ default: module.SettingsHub })));

const routeFallback = <div className="ds-card rounded-2xl border bg-white p-6 text-sm text-slate-500">Loading…</div>;

function Deferred({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={routeFallback}>{children}</Suspense>;
}

function WorkspacePage({ component: Component }: { component: React.ComponentType<{ workspaceId: string; workspace?: string }> }) {
  const { activeWorkspaceId, activeWorkspace } = useWorkspace();
  if (!activeWorkspaceId) return <div className="ds-card rounded-2xl border bg-white p-6 text-sm text-slate-500">Create or select a workspace to continue.</div>;
  return <Deferred><Component workspaceId={activeWorkspaceId} workspace={activeWorkspace?.name ?? ''} /></Deferred>;
}

function WorkspacesRoute() {
  const { activeWorkspaceId, selectWorkspace, refreshWorkspaces } = useWorkspace();
  return <Deferred><Workspaces workspaceId={activeWorkspaceId} onWorkspacesChanged={async (preferredId) => { await refreshWorkspaces(); if (preferredId) selectWorkspace(preferredId); }} onWorkspaceSelected={(workspace) => selectWorkspace(workspace.id)} /></Deferred>;
}

function CalendarRoute() {
  const navigate = useNavigate();
  const { activeWorkspaceId } = useWorkspace();
  return <Deferred><ContentCalendar workspaceId={activeWorkspaceId} onCreatePost={() => navigate('/create-post')} /></Deferred>;
}

export function AppRoutes() {
  return <Routes>
    <Route element={<AppShell />}>
      <Route path="/dashboard" element={<WorkspacePage component={Dashboard} />} />
      <Route path="/workspaces" element={<WorkspacesRoute />} />
      <Route path="/profile" element={<Deferred><Profile /></Deferred>} />
      <Route path="/team" element={<WorkspacePage component={TeamManagement} />} />
      <Route path="/security" element={<Deferred><ChangePassword /></Deferred>} />
      <Route path="/audit-logs" element={<WorkspacePage component={AuditLogs} />} />
      <Route path="/creators" element={<WorkspacePage component={Creators} />} />
      <Route path="/social-accounts" element={<WorkspacePage component={SocialAccounts} />} />
      <Route path="/campaigns" element={<WorkspacePage component={Campaigns} />} />
      <Route path="/calendar" element={<CalendarRoute />} />
      <Route path="/create-post" element={<WorkspacePage component={CreatePostHub} />} />
      <Route path="/review-queue" element={<WorkspacePage component={ReviewQueue} />} />
      <Route path="/publish-jobs" element={<WorkspacePage component={PublishJobs} />} />
      <Route path="/analytics" element={<WorkspacePage component={Analytics} />} />
      <Route path="/assets" element={<WorkspacePage component={AssetLibrary} />} />
      <Route path="/settings" element={<WorkspacePage component={SettingsHub} />} />
    </Route>
    <Route path="/" element={<Navigate to="/dashboard" replace />} />
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>;
}
